import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import DataTablePagination from "./DataTablePagination";
import { useTranslation } from "react-i18next";
import { usePermissions } from "../../../utils/permissions";

/**
 * Page de gestion générique (liste + recherche + filtres + modal
 * ajout/modification + suppression) pour les entités de référence
 * simples de la structure académique / banque de questions.
 *
 * Toute la logique métier (API, champs du formulaire, colonnes du
 * tableau) est fournie en configuration par la page appelante -
 * seul le rendu est mutualisé.
 */
const GenericCrudPage = ({
  title,
  breadcrumbLabel,
  addLabel,
  api, // { list, create, update, remove, restore? }
  columns, // [{ key, label, sortable?, render(row) }]
  fields, // [{ key, label, type, required?, placeholder?, options?, maxLength? }]
  filters = [], // [{ key, label, options: [{value,label}] }]
  searchPlaceholder,
  defaultSort = { by: "name", dir: "asc" },
  buildPayload, // (formData) => payload envoyé à l'API
  getItemLabel = (item) => item.name || item.code,
  hasActiveToggle = true,
  extraFilterParams = () => ({}),
  permissionModule, // ex. "CLASS" -> vérifie CLASS_CREATE / CLASS_UPDATE / CLASS_DELETE
}) => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canCreate = !permissionModule || can(`${permissionModule}_CREATE`);
  const canUpdate = !permissionModule || can(`${permissionModule}_UPDATE`);
  const canDelete = !permissionModule || can(`${permissionModule}_DELETE`);
  const emptyForm = fields.reduce((acc, f) => {
    acc[f.key] = f.type === "checkbox" ? true : f.type === "number" ? "" : "";
    return acc;
  }, {});

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [sortBy, setSortBy] = useState(defaultSort.by);
  const [sortDir, setSortDir] = useState(defaultSort.dir);

  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = {
        page,
        limit,
        sort_by: sortBy,
        sort_dir: sortDir,
        ...filterValues,
        ...extraFilterParams(),
      };
      if (search) params.search = search;
      if (isActiveFilter !== "") params.is_active = isActiveFilter;

      const res = await api.list(params);
      const body = res.data;
      setItems(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("common.crud.loadError"));
      setItems([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sortBy, sortDir, search, isActiveFilter, JSON.stringify(filterValues), t]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortDir("asc");
    }
  };
  const getSortIcon = (field) => (sortBy !== field ? "↕" : sortDir === "asc" ? "↑" : "↓");

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setFilterValues({});
    setIsActiveFilter("");
    setSortBy(defaultSort.by);
    setSortDir(defaultSort.dir);
    setPage(1);
  };

  const openAddModal = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditModal = (item) => {
    setFormMode("edit");
    const data = { ...emptyForm };
    fields.forEach((f) => {
      const v = item[f.key];
      if (f.type === "checkbox") data[f.key] = !!v;
      else if (f.type === "date" && v) data[f.key] = String(v).slice(0, 10);
      else data[f.key] = v ?? "";
    });
    setFormData(data);
    setFormErrors({});
    setSubmitError("");
    setEditingId(item.id);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    fields.forEach((f) => {
      if (f.required && (formData[f.key] === "" || formData[f.key] === null || formData[f.key] === undefined)) {
        errors[f.key] = t("common.crud.fieldRequired", { field: f.label });
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = buildPayload(formData);
      if (formMode === "add") {
        await api.create(payload);
        setFeedback({ type: "success", message: t("common.crud.createSuccess") });
      } else {
        await api.update(editingId, payload);
        setFeedback({ type: "success", message: t("common.crud.updateSuccess") });
      }
      setShowFormModal(false);
      fetchItems();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("common.crud.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.remove(deleteTarget.id);
      setFeedback({ type: "success", message: t("common.crud.deleteSuccess") });
      setDeleteTarget(null);
      if (items.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchItems();
    } catch (err) {
      setFeedback({
        type: "danger",
        message: err?.response?.data?.message || t("common.crud.deleteError"),
      });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.update(item.id, { is_active: !item.is_active });
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, is_active: !item.is_active } : it)));
    } catch (err) {
      setFeedback({ type: "danger", message: t("common.crud.toggleError") });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{breadcrumbLabel}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button type="button" className="btn btn-outline-light bg-white btn-icon me-1" onClick={resetFilters}>
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            <div className="mb-2">
              {canCreate && (
              <button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                <i className="ti ti-square-rounded-plus me-2"></i>
                {addLabel}
              </button>
              )}
            </div>
          </div>
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{title}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="input-icon-start mb-3 me-2 position-relative">
                <span className="icon-addon"></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={searchPlaceholder ?? t("common.crud.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ paddingLeft: "35px", minWidth: "220px" }}
                />
              </div>
              {filters.map((f) => (
                <div className="mb-3 me-2" key={f.key}>
                  <select
                    className="form-select"
                    value={filterValues[f.key] || ""}
                    onChange={(e) => {
                      setFilterValues((prev) => ({ ...prev, [f.key]: e.target.value }));
                      setPage(1);
                    }}
                  >
                    <option value="">{f.label}</option>
                    {f.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
              {hasActiveToggle && (
                <div className="mb-3 me-2">
                  <select
                    className="form-select"
                    value={isActiveFilter}
                    onChange={(e) => {
                      setIsActiveFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">{t("common.crud.allStatuses")}</option>
                    <option value="true">{t("common.crud.filterActive")}</option>
                    <option value="false">{t("common.crud.filterInactive")}</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          <DataTablePagination
            variant="header"
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={(v) => {
              setLimit(v);
              setPage(1);
            }}
          />

          <div className="card-body p-0 py-3">
            <div className="custom-datatable-filter table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        onClick={col.sortable ? () => handleSort(col.key) : undefined}
                        style={col.sortable ? { cursor: "pointer" } : undefined}
                      >
                        {col.label} {col.sortable ? getSortIcon(col.key) : ""}
                      </th>
                    ))}
                    {hasActiveToggle && <th>{t("common.crud.statusColumn")}</th>}
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={columns.length + 2} className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan={columns.length + 2} className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : items.length > 0 ? (
                    items.map((item) => (
                      <tr key={item.id}>
                        {columns.map((col) => (
                          <td key={col.key}>{col.render ? col.render(item) : item[col.key]}</td>
                        ))}
                        {hasActiveToggle && (
                          <td>
                            <span
                              className={`badge badge-soft-${item.is_active ? "success" : "danger"} d-inline-flex align-items-center`}
                              role="button"
                              onClick={() => toggleActive(item)}
                            >
                              <i className="ti ti-circle-filled fs-5 me-1"></i>
                              {item.is_active ? t("common.crud.statusActive") : t("common.crud.statusInactive")}
                            </span>
                          </td>
                        )}
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              as="button"
                              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                            >
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              {canUpdate && (
                              <Dropdown.Item className="rounded-1" onClick={() => openEditModal(item)}>
                                <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                              </Dropdown.Item>
                              )}
                              {hasActiveToggle && canUpdate && (
                                <Dropdown.Item className="rounded-1" onClick={() => toggleActive(item)}>
                                  <i className="ti ti-toggle-right me-2"></i>
                                  {item.is_active ? t("common.crud.deactivate") : t("common.crud.activate")}
                                </Dropdown.Item>
                              )}
                              {canDelete && (
                              <Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(item)}>
                                <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                              </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columns.length + 2} className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("common.crud.noItemsFound")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <DataTablePagination
              variant="footer"
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onLimitChange={(v) => {
                setLimit(v);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{formMode === "add" ? addLabel : t("common.crud.editTitle", { title })}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            {fields.map((f) => (
              <div className="mb-3" key={f.key}>
                <label className="form-label">
                  {f.label} {f.required && <span className="text-danger">*</span>}
                </label>
                {f.type === "select" ? (
                  <select
                    className={`form-select ${formErrors[f.key] ? "is-invalid" : ""}`}
                    value={formData[f.key]}
                    onChange={(e) => handleFormChange(f.key, e.target.value)}
                  >
                    <option value="">{t("common.crud.selectPlaceholder")}</option>
                    {f.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : f.type === "textarea" ? (
                  <textarea
                    className="form-control"
                    rows={2}
                    value={formData[f.key]}
                    onChange={(e) => handleFormChange(f.key, e.target.value)}
                  ></textarea>
                ) : f.type === "checkbox" ? (
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id={`field-${f.key}`}
                      checked={!!formData[f.key]}
                      onChange={(e) => handleFormChange(f.key, e.target.checked)}
                    />
                  </div>
                ) : (
                  <input
                    type={f.type || "text"}
                    className={`form-control ${formErrors[f.key] ? "is-invalid" : ""}`}
                    placeholder={f.placeholder}
                    maxLength={f.maxLength}
                    value={formData[f.key]}
                    onChange={(e) => handleFormChange(f.key, e.target.value)}
                  />
                )}
                {formErrors[f.key] && <div className="invalid-feedback d-block">{formErrors[f.key]}</div>}
              </div>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : formMode === "add" ? t("common.crud.add") : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={deleteTarget !== null} onHide={() => (deleting ? null : setDeleteTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-trash-x"></i>
          </span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p>
            {t("common.crud.voulezVousVraimentSupprimer")} <strong>{deleteTarget ? getItemLabel(deleteTarget) : ""}</strong> ?
          </p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("common.crud.cancel")}
            </button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GenericCrudPage;
